import { Injectable } from '@angular/core';
import { WindowReferenceService } from './window-reference.service';
import { environment } from 'src/environments/environment';
import { DataLayer, DataLayerItem, Addon } from '../models';
import { BehaviorSubject } from 'rxjs';
import { compareObjects } from '../utils/object-utils';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private window: Window | null;
  private googleTagManagerId = environment.googleTagManagerIdChile;
  private dataLayerItems = new BehaviorSubject<DataLayerItem[]>([]);
  currentItems = this.dataLayerItems.asObservable();
  private addons = new BehaviorSubject<Addon[]>([]);
  currentAddons = this.addons.asObservable();

  constructor(windowRef: WindowReferenceService) {
    this.window = windowRef.nativeWindow;
    this.init();
  }

  /**
   * Inicializa el data layer de Google Tag Manager, inyectando
   * script en el head del documento
   */
  public init() {
    try {
      const dataLayerScript = document.createElement('script');
      dataLayerScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${this.googleTagManagerId}');`;

      const dataLayerInitScript = document.createElement('script');
      dataLayerInitScript.innerHTML = `window.dataLayer = window.dataLayer || [];`;

      document.head.appendChild(dataLayerScript);
      document.head.appendChild(dataLayerInitScript);
    } catch (e) {
      console.warn('Failed to initialize Google Tag Manager:', e);
    }
  }

  /**
   * Envia un evento al data layer
   * @param obj
   */
  private ping(obj: any) {
    if (obj) {
      this.window.dataLayer.push(obj);
    }
  }

  /**
   * Registra un evento en el data layer, se debe enviar un DataLayer
   * @param data
   */
  public logEvent(data: DataLayer) {
    this.ping(data);
  }

  /**
   * Agrega un item al array de dataLayerItems, usado para manejar el estado de los items
   * que se conserva entre componentes
   * @param item
   */
  public addItem(item: DataLayerItem) {
    const currentItems = this.dataLayerItems.getValue();
    const existingItem = currentItems.find((i) =>
      compareObjects(i, item, ['quantity'])
    );

    if (!existingItem) {
      this.dataLayerItems.next([...currentItems, item]);
    } else {
      existingItem.quantity += item.quantity;
    }
  }

  /**
   * Remueve un item del array de dataLayerItems, o disminuye la cantidad de un item existente,
   * usado principalmente para la adicion o remocion de asientos en la vista de asientos
   * @param item
   */
  public removeItem(item: DataLayerItem) {
    const currentItems = this.dataLayerItems.getValue();
    const existingItem = currentItems.find((i) =>
      compareObjects(i, item, ['quantity'])
    );

    if (existingItem) {
      if (existingItem.quantity == 0) {
        const updatedItems = currentItems.filter(
          (i) => !compareObjects(i, item)
        );
        this.dataLayerItems.next(updatedItems);
      } else {
        existingItem.quantity -= item.quantity;
      }
    }
  }

  /**
   * Actualiza el array de dataLayerItems, reemplaza el array actual por el nuevo
   * @deprecated
   * @param items
   */
  public updateItems(items: DataLayerItem[]) {
    this.dataLayerItems.next(items);
  }

  /**
   * Obtiene el array de dataLayerItems, usado para obtener el estado de los items
   * que se conserva entre componentes
   * @returns DataLayerItem[]
   */
  public getItems(): DataLayerItem[] {
    return this.dataLayerItems.getValue();
  }

  /**
   * Limpia el array de dataLayerItems, reemplaza el array actual por un array vacio
   */
  public clearItems() {
    this.dataLayerItems.next([]);
  }

  /**
   * Agrega un array de addons, usado para manejar el estado de los addons
   * que se conserva entre componentes
   * @param addons
   */
  public addAddons(addons: Addon[]) {
    this.addons.next(addons);
  }

  /**
   * Guardar addons en localStorage
   */
  public saveAddons() {
    localStorage.setItem('addons', JSON.stringify(this.getAddons()));
  }

  /**
   * Obtener addons desde localStorage
   */
  public getAddonsFromLocalStorage() {
    const addons = localStorage.getItem('addons');
    if (addons) {
      this.addons.next(JSON.parse(addons));
    }
  }

  /**
   * Limpia el array de addons, reemplaza el array actual por un array vacio
   */
  public clearAddons() {
    this.addons.next([]);
  }

  /**
   * Obtiene el array de addons, usado para obtener el estado de los addons
   * que se conserva entre componentes
   * @returns Addon[]
   */
  public getAddons(): Addon[] {
    return this.addons.getValue();
  }

  /**
   * Construye un objeto DataLayerInfo, usado para enviar un evento al data layer
   * Se siguen reglas de negocio especificas para cada evento.
   *
   * @param event el nombre del evento
   * @param items los items que se envian
   * @param data los datos adicionales que se envian, especificos para cada evento
   * @returns DataLayerInfo
   */
  public constructDataLayerInfo(
    event: string,
    items: DataLayerItem[],
    data: any = {},
    add_ons?: any
  ): DataLayer {
    const dataLayerInfoBase: DataLayer = {
      event,
      ecommerce: {
        items,
        ...data,
      },
    };
    if (add_ons) {
      dataLayerInfoBase['add_ons'] = add_ons;
    }
    return dataLayerInfoBase;
  }
}
